import { useCallback, useEffect, useState } from "react";
import { collection, deleteDoc, deleteField, doc, getDoc, getDocs, query, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { v4 } from "uuid";
import { toast } from "../Toast.jsx";
import { auth, db, storage } from "../../firebase/config.js";
import { FREE_PRODUCT_LIMIT } from "../../plans.js";
import { MAX_IMAGE_BYTES, PUBLIC_FIELDS, RESERVED_USERNAMES, publicMenu } from "../../menu.js";
import { COLOR_PATTERN, cleanLine, textProblem } from "../../businessValidation.js";
import { coverFileProblem, shrinkCover } from "../../cover.js";
import { extraPhotos, mainPath, PHOTO_MAX_WIDTH, photoFields } from "../../gallery.js";
import { cleanPromo, MAX_PROMOS, promoProblem, promosAvailable, promosOf } from "../../promos.js";
import { PROMOS_PRO_ONLY } from "../../plans.js";

export const notify = (message) => toast.success(message);
export const notifyError = (message) => toast.error(message);

const uploadImage = async (file) => {
    const id = v4();
    const path = `images/${auth.currentUser.uid}/${id}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return { url: await getDownloadURL(storageRef), id, path };
}

// Borrar las imágenes viejas es lo de menos: si falla, el producto ya quedó bien.
const removePath = async (path) => {
    if (!path) return;
    try {
        await deleteObject(ref(storage, path));
    } catch (error) {
        console.error(error);
    }
}

// Todas las fotos de un producto: la principal y las de la galería.
const removeImage = (product) => {
    removePath(mainPath(product));
    extraPhotos(product).forEach((f) => removePath(f.path || f.url));
}

// Sube las fotos nuevas del formulario (achicadas) y devuelve la lista final en orden:
// [{ url, path }]. Las que ya estaban subidas quedan como están.
const uploadPhotos = (photos) => Promise.all(photos.map(async (p) => {
    if (!p.file) return { url: p.url, path: p.path };
    const small = await shrinkCover(p.file, PHOTO_MAX_WIDTH, "foto.jpg");
    if (small.size > MAX_IMAGE_BYTES) throw new Error("foto-pesada");
    const image = await uploadImage(small);
    return { url: image.url, path: image.path };
}));

// Firestore no acepta null en image (las reglas piden un link): si no quedan fotos se borra el campo.
const productWrite = (fields) => Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v === null ? deleteField() : v]));
const productState = (fields) => Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== null));

// Imágenes guardadas solo por su link (logo, portada): ref() acepta el link de descarga.
const removeImageByUrl = async (url) => {
    if (!url) return;
    try {
        await deleteObject(ref(storage, url));
    } catch (error) {
        console.error(error);
    }
}

// Firestore acepta hasta 500 escrituras por batch.
const BATCH_SIZE = 400;
const commitInChunks = async (ops) => {
    for (let i = 0; i < ops.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        ops.slice(i, i + BATCH_SIZE).forEach((op) => op(batch));
        await batch.commit();
    }
}

// La última importación (modo sumar) o el último cambio de precios, para poder deshacerlos
// durante 24 horas desde este navegador.
export const UNDO_HOURS = 24;
const IMPORT_KEY = "miqrapp-importacion:";
const PRICES_KEY = "miqrapp-precios:";
const undoKey = (uid, prefix = IMPORT_KEY) => prefix + uid;
export const readLastImport = (uid, prefix) => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(undoKey(uid, prefix)) || "null");
        return saved && Date.now() - saved.at < UNDO_HOURS * 3600 * 1000 ? saved : null;
    } catch (error) {
        return null;
    }
}
const saveLastImport = (uid, value, prefix) => {
    try {
        if (value) window.localStorage.setItem(undoKey(uid, prefix), JSON.stringify(value));
        else window.localStorage.removeItem(undoKey(uid, prefix));
    } catch (error) {
        console.error(error);
    }
}

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Datos del negocio logueado y todo lo que el panel puede cambiar.
// Cada acción devuelve true si salió bien, y muestra el aviso correspondiente.
const useBusiness = (email) => {

    const [business, setBusiness] = useState(null);
    const [products, setProducts] = useState([]);
    const [status, setStatus] = useState("loading");

    const load = useCallback(async () => {
        try {
            const snapshot = await getDoc(doc(db, "users", email));
            const data = snapshot.data();

            if (!data?.registered) {
                setBusiness(null);
                setStatus("unregistered");
                return;
            }

            // El menú público es una copia: si falta o quedó desactualizada (por ejemplo,
            // negocios de antes de que existiera), se rehace con los datos del dueño.
            const menuRef = doc(db, "menus", data.usuarioEmpresa);
            const menu = (await getDoc(menuRef)).data();
            const fresh = publicMenu(data);
            if (!menu || PUBLIC_FIELDS.some((key) => !sameValue(menu[key], fresh[key]))) {
                await setDoc(menuRef, fresh).catch(console.error);
            }

            const productDocs = await getDocs(query(collection(db, "products"), where("userID", "==", data.id)));
            setBusiness(data);
            setProducts(productDocs.docs.map((d) => d.data()));
            setStatus("ready");
        } catch (error) {
            console.error(error);
            setStatus("error");
        }
    }, [email]);

    useEffect(() => {
        load();
    }, [load]);

    const userRef = () => doc(db, "users", email);

    // Los cambios van al doc privado y a la copia pública del menú, juntos.
    const saveBoth = (changes) => {
        const batch = writeBatch(db);
        batch.update(userRef(), changes);
        const shown = Object.fromEntries(Object.entries(changes).filter(([key]) => PUBLIC_FIELDS.includes(key)));
        if (Object.keys(shown).length) batch.update(doc(db, "menus", business.usuarioEmpresa), shown);
        return batch.commit();
    }
    const categories = Array.isArray(business?.categories) ? business.categories : [];
    const canAddProduct = business?.subscribed || products.length < FREE_PRODUCT_LIMIT;

    const register = async (fields, logoFile) => {
        if (RESERVED_USERNAMES.includes(fields.usuarioEmpresa)) {
            notifyError("Ese nombre de usuario no está disponible. Probá con otro.");
            return false;
        }
        try {
            const menuRef = doc(db, "menus", fields.usuarioEmpresa);
            if ((await getDoc(menuRef)).exists()) {
                notifyError("Ya existe un negocio con ese nombre de usuario. Probá con otro.");
                return false;
            }

            const logo = await uploadImage(logoFile);
            const data = {
                ...fields,
                id: auth.currentUser.uid,
                logo: logo.url,
                email: auth.currentUser.email,
                registered: true,
                subscribed: false
            };
            // En un solo batch: si otro negocio tomó el nombre recién, no se guarda nada.
            const batch = writeBatch(db);
            batch.set(userRef(), data);
            batch.set(menuRef, publicMenu(data));
            await batch.commit();
            await load();
            notify("¡Listo! Ya podés armar tu menú.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos guardar los datos. Probá de nuevo.");
            return false;
        }
    }

    const saveBusiness = async (fields, logoFile) => {
        try {
            const changes = { ...fields };
            if (logoFile) {
                changes.logo = (await uploadImage(logoFile)).url;
            }
            await saveBoth(changes);
            setBusiness((b) => ({ ...b, ...changes }));
            notify("Datos del negocio actualizados.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos guardar los cambios. Probá de nuevo.");
            return false;
        }
    }

    const saveColors = async (colors) => {
        if (!Object.values(colors).every((c) => COLOR_PATTERN.test(c))) {
            notifyError("Alguno de los colores no es válido. Elegilo de nuevo.");
            return false;
        }
        try {
            await saveBoth(colors);
            setBusiness((b) => ({ ...b, ...colors }));
            notify("Colores del menú guardados.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos guardar los colores. Probá de nuevo.");
            return false;
        }
    }

    // Logo y color del QR (campos qrLogo y qrColor, solo en el doc privado: el QR se arma en el panel).
    const saveQrStyle = async (changes) => {
        const clean = {};
        if ("qrLogo" in changes) clean.qrLogo = changes.qrLogo === true;
        if ("qrColor" in changes) clean.qrColor = changes.qrColor === "marca" ? "marca" : "negro";
        const before = { qrLogo: business.qrLogo, qrColor: business.qrColor };
        setBusiness((b) => ({ ...b, ...clean }));
        try {
            await updateDoc(userRef(), clean);
            return true;
        } catch (error) {
            console.error(error);
            setBusiness((b) => ({ ...b, ...before }));
            notifyError("No pudimos guardar el estilo del QR. Probá de nuevo.");
            return false;
        }
    }

    // Portada del menú (Pro). Se achica antes de subir y se borra la anterior.
    const saveCover = async (file) => {
        if (business?.subscribed !== true) {
            notifyError("La portada es del plan Pro.");
            return false;
        }
        const problem = coverFileProblem(file);
        if (problem) {
            notifyError(problem);
            return false;
        }
        try {
            const small = await shrinkCover(file);
            if (small.size > MAX_IMAGE_BYTES) {
                notifyError("La foto pesa más de 5 MB y no pudimos achicarla. Probá con una JPG más liviana.");
                return false;
            }
            const old = business.portada;
            const portada = (await uploadImage(small)).url;
            await saveBoth({ portada });
            setBusiness((b) => ({ ...b, portada }));
            removeImageByUrl(old);
            notify("Portada guardada. Ya se ve en tu menú.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos guardar la portada. Probá de nuevo.");
            return false;
        }
    }

    const removeCover = async () => {
        try {
            const old = business.portada;
            await saveBoth({ portada: "" });
            setBusiness((b) => ({ ...b, portada: "" }));
            removeImageByUrl(old);
            notify("Sacamos la portada de tu menú.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos sacar la portada. Probá de nuevo.");
            return false;
        }
    }

    // ---- Promociones del banner (Pro) ----
    const promoList = promosOf(business);

    const writePromos = async (promos) => {
        await saveBoth({ promos });
        setBusiness((b) => ({ ...b, promos }));
    }

    // Crea o cambia una promo. `file` es la foto nueva (se achica antes de subir);
    // `dropImage` saca la foto que tenía.
    const savePromo = async (promo, file, dropImage) => {
        if (!promosAvailable(business, PROMOS_PRO_ONLY)) {
            notifyError("Las promociones son del plan Pro.");
            return false;
        }
        const clean = cleanPromo(promo);
        const problem = promoProblem(clean);
        if (problem) {
            notifyError(problem);
            return false;
        }
        const old = promoList.find((p) => p.id === clean.id);
        if (!old && promoList.length >= MAX_PROMOS) {
            notifyError(`Podés tener hasta ${MAX_PROMOS} promociones. Borrá una para sumar otra.`);
            return false;
        }
        try {
            if (file) {
                const problemFile = coverFileProblem(file);
                if (problemFile) {
                    notifyError(problemFile);
                    return false;
                }
                const small = await shrinkCover(file, PHOTO_MAX_WIDTH, "promo.jpg");
                if (small.size > MAX_IMAGE_BYTES) {
                    notifyError("La foto pesa más de 5 MB y no pudimos achicarla. Probá con una JPG más liviana.");
                    return false;
                }
                clean.imagen = (await uploadImage(small)).url;
            } else if (dropImage) {
                clean.imagen = "";
            } else {
                clean.imagen = old?.imagen || "";
            }
            const next = old ? promoList.map((p) => p.id === clean.id ? clean : p) : [...promoList, clean];
            await writePromos(next);
            if (old?.imagen && old.imagen !== clean.imagen) removeImageByUrl(old.imagen);
            notify(old ? "Promo guardada." : "¡Promo creada! Ya se ve en tu menú cuando le toca.");
            return true;
        } catch (error) {
            console.error(error);
            if (file && clean.imagen && clean.imagen !== old?.imagen) removeImageByUrl(clean.imagen);
            notifyError("No pudimos guardar la promo. Probá de nuevo.");
            return false;
        }
    }

    // Orden y pausa: se guarda la lista entera.
    const reorderPromos = async (promos) => {
        const before = business.promos;
        setBusiness((b) => ({ ...b, promos }));
        try {
            await saveBoth({ promos });
            return true;
        } catch (error) {
            console.error(error);
            setBusiness((b) => ({ ...b, promos: before }));
            notifyError("No pudimos guardar el cambio. Probá de nuevo.");
            return false;
        }
    }

    const deletePromo = async (promo) => {
        try {
            await writePromos(promoList.filter((p) => p.id !== promo.id));
            removeImageByUrl(promo.imagen);
            notify("Promo borrada.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos borrar la promo. Probá de nuevo.");
            return false;
        }
    }

    const setOrders = async (pedidos) => {
        setBusiness((b) => ({ ...b, pedidos }));
        try {
            await saveBoth({ pedidos });
            notify(pedidos ? "Listo, tus clientes ya pueden pedirte por WhatsApp." : "Pedidos por WhatsApp desactivados.");
            return true;
        } catch (error) {
            console.error(error);
            setBusiness((b) => ({ ...b, pedidos: !pedidos }));
            notifyError("No pudimos cambiar los pedidos. Probá de nuevo.");
            return false;
        }
    }

    // null = a confirmar por WhatsApp, 0 = gratis, o el monto fijo que suma el carrito.
    const setDeliveryCost = async (costoEnvio) => {
        const before = business.costoEnvio ?? null;
        setBusiness((b) => ({ ...b, costoEnvio }));
        try {
            await saveBoth({ costoEnvio });
            notify(costoEnvio === null ? "Listo, el costo del envío lo confirmás por WhatsApp." :
                costoEnvio === 0 ? "Listo, el envío figura gratis en el carrito." : "Costo del envío guardado.");
            return true;
        } catch (error) {
            console.error(error);
            setBusiness((b) => ({ ...b, costoEnvio: before }));
            notifyError("No pudimos guardar el costo del envío. Probá de nuevo.");
            return false;
        }
    }

    // Reseñas de Google: el link para dejar reseña y si se muestra la ficha de Maps.
    const saveReviews = async (changes) => {
        try {
            await saveBoth(changes);
            setBusiness((b) => ({ ...b, ...changes }));
            notify("Reseñas del menú guardadas.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos guardar las reseñas. Probá de nuevo.");
            return false;
        }
    }

    const addCategory = async (name) => {
        const clean = cleanLine(name);
        if (!clean) return false;
        const problem = textProblem("La categoría", clean, 40);
        if (problem) {
            notifyError(problem);
            return false;
        }
        if (categories.some((c) => c.toLowerCase() === clean.toLowerCase())) {
            notifyError("Ya tenés una categoría con ese nombre.");
            return false;
        }
        try {
            const updated = [...categories, clean];
            await saveBoth({ categories: updated });
            setBusiness((b) => ({ ...b, categories: updated }));
            notify(`Categoría "${clean}" creada.`);
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos crear la categoría. Probá de nuevo.");
            return false;
        }
    }

    // Borra la categoría y todos sus productos, como hacía la configuración anterior.
    const deleteCategory = async (name) => {
        try {
            const toDelete = products.filter((p) => p.category === name);
            await Promise.all(toDelete.map((p) => deleteDoc(doc(db, "products", p.id))));
            toDelete.forEach(removeImage);

            const updated = categories.filter((c) => c !== name);
            await saveBoth({ categories: updated });
            setBusiness((b) => ({ ...b, categories: updated }));
            setProducts((list) => list.filter((p) => p.category !== name));
            notify(`Categoría "${name}" eliminada.`);
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos eliminar la categoría. Probá de nuevo.");
            await load();
            return false;
        }
    }

    const photoError = (error) => error?.message === "foto-pesada"
        ? "Una de las fotos pesa más de 5 MB y no pudimos achicarla. Probá con una JPG más liviana."
        : null;

    // `photos`: la lista del formulario ({ url, path, file }), la principal primero.
    const createProduct = async (fields, photos) => {
        if (!canAddProduct) {
            notifyError(`El plan Gratis permite hasta ${FREE_PRODUCT_LIMIT} productos. Pasate a Pro para cargar más.`);
            return false;
        }
        try {
            const uploaded = await uploadPhotos(photos);
            const product = productState({
                ...fields,
                id: v4(),
                ...photoFields(uploaded).fields,
                agotado: false,
                userID: business.id
            });
            await setDoc(doc(db, "products", product.id), product);
            setProducts((list) => [...list, product]);
            notify("¡Producto creado!");
            return true;
        } catch (error) {
            console.error(error);
            notifyError(photoError(error) || "No pudimos crear el producto. Probá de nuevo.");
            return false;
        }
    }

    // `photos` como en createProduct; si es undefined las fotos no cambian.
    const updateProduct = async (product, fields, photos) => {
        try {
            let changes = { ...fields };
            let removed = [];
            if (photos) {
                const before = [{ path: mainPath(product) }, ...extraPhotos(product).map((f) => ({ path: f.path || f.url }))];
                const result = photoFields(await uploadPhotos(photos), before);
                changes = { ...changes, ...result.fields };
                removed = result.removed;
            }
            await updateDoc(doc(db, "products", product.id), productWrite(changes));
            removed.forEach(removePath);
            setProducts((list) => list.map((p) => {
                if (p.id !== product.id) return p;
                const next = { ...p, ...changes };
                Object.keys(changes).forEach((k) => changes[k] === null && delete next[k]);
                return next;
            }));
            notify("Producto actualizado.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError(photoError(error) || "No pudimos guardar el producto. Probá de nuevo.");
            return false;
        }
    }

    const toggleAgotado = async (product) => {
        const agotado = !product.agotado;
        if (agotado && !business.subscribed) {
            notifyError("Marcar productos como agotados es del plan Pro.");
            return false;
        }
        setProducts((list) => list.map((p) => p.id === product.id ? { ...p, agotado } : p));
        try {
            await updateDoc(doc(db, "products", product.id), { agotado });
            return true;
        } catch (error) {
            console.error(error);
            setProducts((list) => list.map((p) => p.id === product.id ? { ...p, agotado: !agotado } : p));
            notifyError("No pudimos cambiar el estado. Probá de nuevo.");
            return false;
        }
    }

    // Varios productos de una (lo usa el asistente). Marcar agotado es de Pro, igual que arriba.
    const setAgotadoMany = async (list, agotado) => {
        if (agotado && !business.subscribed) {
            notifyError("Marcar productos como agotados es del plan Pro.");
            return false;
        }
        if (!list.length) return false;
        try {
            await commitInChunks(list.map((p) => (batch) => batch.update(doc(db, "products", p.id), { agotado })));
            const ids = new Set(list.map((p) => p.id));
            setProducts((all) => all.map((p) => ids.has(p.id) ? { ...p, agotado } : p));
            notify(agotado ? (list.length === 1 ? "Listo, quedó como agotado." : `Listo, ${list.length} productos quedaron como agotados.`) : (list.length === 1 ? "Listo, ya está disponible." : `Listo, ${list.length} productos volvieron a estar disponibles.`));
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos cambiar el estado. Probá de nuevo.");
            await load();
            return false;
        }
    }

    const deleteProduct = async (product) => {
        try {
            await deleteDoc(doc(db, "products", product.id));
            removeImage(product);
            setProducts((list) => list.filter((p) => p.id !== product.id));
            notify("Producto eliminado.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos eliminar el producto. Probá de nuevo.");
            return false;
        }
    }

    const [lastImport, setLastImport] = useState(null);
    useEffect(() => {
        if (business?.id) setLastImport(readLastImport(business.id));
    }, [business?.id]);

    // Aplica el plan de importarCatalogo.planImport. En "reemplazar" primero crea lo nuevo
    // y recién al final borra lo viejo, así un corte a mitad de camino no deja el menú vacío.
    const importCatalog = async (plan) => {
        try {
            const created = plan.create.map((fields) => ({ ...fields, id: v4(), userID: business.id }));
            const ops = [
                ...created.map((p) => (batch) => batch.set(doc(db, "products", p.id), p)),
                ...plan.update.map(({ product, changes }) => (batch) => batch.update(doc(db, "products", product.id), changes))
            ];
            await commitInChunks(ops);
            if (JSON.stringify(plan.categories) !== JSON.stringify(categories)) await saveBoth({ categories: plan.categories });
            if (plan.remove.length) {
                await commitInChunks(plan.remove.map((p) => (batch) => batch.delete(doc(db, "products", p.id))));
                plan.remove.forEach(removeImage);
            }

            const changed = new Map(plan.update.map((u) => [u.product.id, u.changes]));
            const removed = new Set(plan.remove.map((p) => p.id));
            setProducts((list) => [...list.filter((p) => !removed.has(p.id)).map((p) => changed.has(p.id) ? { ...p, ...changed.get(p.id) } : p), ...created]);
            setBusiness((b) => ({ ...b, categories: plan.categories }));

            if (plan.mode === "sumar") {
                const record = {
                    at: Date.now(),
                    count: created.length + plan.update.length,
                    created: created.map((p) => p.id),
                    updated: plan.update.map(({ product, changes }) => ({
                        id: product.id,
                        prev: Object.fromEntries(Object.keys(changes).map((k) => [k, product[k] ?? null]))
                    })),
                    categoriesBefore: categories
                };
                saveLastImport(business.id, record);
                setLastImport(record);
            } else {
                saveLastImport(business.id, null);
                setLastImport(null);
            }
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos terminar de importar. Revisá tus productos y probá de nuevo.");
            await load();
            return false;
        }
    }

    // Borra lo que creó la última importación y devuelve a lo que había lo que cambió.
    // Un campo que antes no existía (por ejemplo, una descripción nueva) se deja como quedó.
    const undoImport = async () => {
        const record = lastImport;
        if (!record) return false;
        try {
            const restore = (prev) => Object.fromEntries(Object.entries(prev)
                .map(([k, v]) => [k, v === null ? (k === "agotado" ? false : k === "etiquetas" ? [] : undefined) : v])
                .filter(([, v]) => v !== undefined));
            const createdIds = new Set(record.created);
            const existingIds = new Set(products.map((p) => p.id));
            const updates = record.updated.filter((u) => existingIds.has(u.id)).map((u) => ({ id: u.id, changes: restore(u.prev) }))
                .filter((u) => Object.keys(u.changes).length);
            await commitInChunks([
                ...record.created.filter((id) => existingIds.has(id)).map((id) => (batch) => batch.delete(doc(db, "products", id))),
                ...updates.map((u) => (batch) => batch.update(doc(db, "products", u.id), u.changes))
            ]);

            const remaining = products.filter((p) => !createdIds.has(p.id));
            const stillUsed = categories.filter((c) => !record.categoriesBefore.includes(c) && remaining.some((p) => p.category === c));
            const restoredCategories = [...record.categoriesBefore, ...stillUsed];
            if (JSON.stringify(restoredCategories) !== JSON.stringify(categories)) await saveBoth({ categories: restoredCategories });

            const changed = new Map(updates.map((u) => [u.id, u.changes]));
            setProducts(remaining.map((p) => changed.has(p.id) ? { ...p, ...changed.get(p.id) } : p));
            setBusiness((b) => ({ ...b, categories: restoredCategories }));
            saveLastImport(business.id, null);
            setLastImport(null);
            notify("Listo, deshicimos la importación.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos deshacer la importación. Probá de nuevo.");
            await load();
            return false;
        }
    }

    const dismissLastImport = () => {
        saveLastImport(business.id, null);
        setLastImport(null);
    }

    // Último cambio masivo de precios, para deshacerlo durante 24 horas (igual que la importación).
    const [lastPriceChange, setLastPriceChange] = useState(null);
    useEffect(() => {
        if (business?.id) setLastPriceChange(readLastImport(business.id, PRICES_KEY));
    }, [business?.id]);

    // update: [{ product, price }] de bulkPrice.planPriceChange. Va todo junto en batches.
    const changePrices = async (update, description) => {
        if (business.subscribed !== true) {
            notifyError("Cambiar precios de a muchos es del plan Pro.");
            return false;
        }
        if (!update.length) return false;
        try {
            await commitInChunks(update.map(({ product, price }) => (batch) => batch.update(doc(db, "products", product.id), { price })));
            const changed = new Map(update.map((u) => [u.product.id, u.price]));
            setProducts((list) => list.map((p) => changed.has(p.id) ? { ...p, price: changed.get(p.id) } : p));
            const record = {
                at: Date.now(),
                count: update.length,
                description,
                items: update.map(({ product, price }) => ({ id: product.id, prev: product.price ?? null, next: price }))
            };
            saveLastImport(business.id, record, PRICES_KEY);
            setLastPriceChange(record);
            notify(update.length === 1 ? "Listo, cambiamos 1 precio." : `Listo, cambiamos ${update.length} precios.`);
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos cambiar los precios. Probá de nuevo.");
            await load();
            return false;
        }
    }

    // Vuelve al precio anterior solo los que siguen con el precio del cambio
    // (si después editaste uno a mano, ese se respeta).
    const undoPriceChange = async () => {
        const record = lastPriceChange;
        if (!record) return false;
        try {
            const current = new Map(products.map((p) => [p.id, p]));
            const items = record.items.filter((i) => current.has(i.id) && sameValue(current.get(i.id).price, i.next) && i.prev !== null);
            await commitInChunks(items.map((i) => (batch) => batch.update(doc(db, "products", i.id), { price: i.prev })));
            const restored = new Map(items.map((i) => [i.id, i.prev]));
            setProducts((list) => list.map((p) => restored.has(p.id) ? { ...p, price: restored.get(p.id) } : p));
            saveLastImport(business.id, null, PRICES_KEY);
            setLastPriceChange(null);
            notify("Listo, volvieron los precios de antes.");
            return true;
        } catch (error) {
            console.error(error);
            notifyError("No pudimos deshacer el cambio de precios. Probá de nuevo.");
            await load();
            return false;
        }
    }

    const dismissLastPriceChange = () => {
        saveLastImport(business.id, null, PRICES_KEY);
        setLastPriceChange(null);
    }

    return {
        lastImport, importCatalog, undoImport, dismissLastImport,
        lastPriceChange, changePrices, undoPriceChange, dismissLastPriceChange,
        business, products, categories, status, canAddProduct, reload: load,
        register, saveBusiness, saveColors, saveQrStyle, saveCover, removeCover, savePromo, reorderPromos, deletePromo, setOrders, setDeliveryCost, saveReviews, addCategory, deleteCategory,
        createProduct, updateProduct, toggleAgotado, setAgotadoMany, deleteProduct
    };
}

export default useBusiness
